// @flow
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AiBadge, CallHistoryItemsQuery, NoData, Portal, useCallHistoryAiAnalysisQuery } from '../../../../../shared';
import { Box, Card, Chip, Divider, Drawer, IconButton, Stack, Tab as MuiTab, Tabs, Typography } from '@mui/material';
import { Check, Copy, Sparkle, X } from '@phosphor-icons/react';
import { CallPlayer } from './call-player';
import { getTimelines } from './call-timelines/lib';
import { Timelines } from './call-timelines';
import { CallAiAnalysis } from './call-ai-analysis';
import { Preloader } from '@coverright/features';
import { CallQaPanel } from './qa';

type Props = {
  call?: CallHistoryItemsQuery['callHistoryItems']['data'][0],
  open: boolean,
  onClose: () => void,
};

export const CallAiAnalysisDrawer = ({ call, onClose, open }: Props) => {
  const [tab, setTab] = useState(0);
  const player = useRef<any>(null);

  const { data: aiAnalysisData, loading } = useCallHistoryAiAnalysisQuery({
    variables: { callHistory: call?.id },
    skip: !call?.hasAIAnalysis,
    notifyOnNetworkStatusChange: true,
  });

  const aiAnalysis: { mlDataID: string; mlData: any }[] | undefined = useMemo(() => (
    aiAnalysisData?.callHistoryAIAnalysis?.aiAnalysis
      ? JSON.parse(aiAnalysisData!.callHistoryAIAnalysis!.aiAnalysis)
      : undefined
  ), [aiAnalysisData?.callHistoryAIAnalysis?.aiAnalysis]);

  const timelines = getTimelines(aiAnalysis);
  const emotion: number | undefined = aiAnalysis?.find(i => i.mlDataID === 'meetingEmotion')?.mlData.recordingAverageValue;
  const interaction: number | undefined = aiAnalysis?.find(i => i.mlDataID === 'languageInteraction')?.mlData.recordingAverageValue;

  useEffect(() => { setTab(0); }, [call?.id]);

  const maskedTranscript = call?.maskedTranscript;
  const callSummary = call?.callHistoryAnalysis?.summary;
  const callType = call?.callType;

  return (
    <Drawer anchor={'right'} open={open} onClose={() => onClose()}>
      <Card sx={{ p: 4, minWidth: 500, width: '40vw', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant={'h3'}>Call AI analysis</Typography>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3, mt: 1 }} />

        <Stack gap={2} flex={1}>
          <CallPlayer id={call?.audioRecordId} ref={player} />

          {loading && <Preloader sx={{ height: 150 }} />}

          {!loading && <>
            {aiAnalysis && <>
              <Stack gap={1} direction={'row'} alignItems={'center'}>
                <Typography flex={1} variant={'subtitle1'}>Coaching</Typography>
                <CoachingChip label={'Emotion'} value={emotion} />
                <CoachingChip label={'Interaction'} value={interaction} />
              </Stack>
              {timelines && <Timelines timelines={timelines} onClick={amount => player.current?.seekTo(amount)} />}
              <CallAiAnalysis call={call} loading={loading} />
            </>}

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant={'scrollable'}
              scrollButtons={'auto'}
              allowScrollButtonsMobile
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  minWidth: 0,
                  minHeight: 40,
                  px: 1.25,
                  fontSize: 13,
                  textTransform: 'none',
                },
              }}
            >
              <MuiTab
                label="AI Summary"
                iconPosition={'start'}
                icon={<Sparkle size={15} weight={'fill'} color={tab === 0 ? '#7C3AED' : 'currentColor'} />}
              />
              <MuiTab label="Transcription" />
              <MuiTab label="Masked" />
              <MuiTab
                label="QA & Compliance"
                iconPosition={'start'}
                icon={<Sparkle size={15} weight={'fill'} color={tab === 3 ? '#7C3AED' : 'currentColor'} />}
                sx={{ '&.Mui-selected': { color: '#7C3AED' } }}
              />
            </Tabs>

            {tab === 0 && (
              <Stack gap={1.5}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <Typography variant={'h5'} flex={1}>AI Call Summary</Typography>
                  <AiBadge />
                  {callType && (
                    <Chip
                      label={String(callType).charAt(0).toUpperCase() + String(callType).slice(1).toLowerCase().replace(/_/g, ' ')}
                      size={'small'}
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Stack>
                {callSummary == null
                  ? <NoData>Summary not yet processed</NoData>
                  : <Portal p={2}>
                      <Typography variant={'body2'} color={'text.secondary'} whiteSpace={'pre-line'}>
                        {callSummary}
                      </Typography>
                    </Portal>
                }
              </Stack>
            )}

            {tab === 1 && (
              call?.transcription
                ? <Portal p={2}>
                    <Typography whiteSpace={'pre-line'} variant={'body2'}
                                dangerouslySetInnerHTML={{ __html: call.transcription as string }} />
                  </Portal>
                : <NoData />
            )}

            {tab === 2 && (
              <Stack gap={1.5}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <AiBadge label={'PII Masked'} />
                  <Typography variant={'caption'} color={'text.secondary'} flex={1}>
                    Names, numbers and personal info replaced with placeholders
                  </Typography>
                  {maskedTranscript != null && <CopyButton text={maskedTranscript} />}
                </Stack>
                {maskedTranscript == null
                  ? <NoData>Masked transcript not yet available</NoData>
                  : <Portal p={2}>
                      <Typography whiteSpace={'pre-line'} variant={'body2'} color={'text.secondary'}>
                        {maskedTranscript}
                      </Typography>
                    </Portal>
                }
              </Stack>
            )}

            {tab === 3 && <CallQaPanel call={call} />}
          </>}
        </Stack>
      </Card>
    </Drawer>
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <IconButton size={'small'} onClick={handleCopy} title={'Copy to clipboard'}>
      {copied ? <Check size={16} color={'#056048'} /> : <Copy size={16} />}
    </IconButton>
  );
};

const CoachingChip = ({ label, value }: { label: React.ReactNode; value?: number }) => {
  if (!value) return null;
  const color = value < 33 ? 'error' : value > 66 ? 'success' : 'warning';
  return (
    <Chip
      sx={{ fontSize: 11 }}
      label={<>{label} <b>{value}</b></>}
      size={'small'}
      variant={'filled'}
      color={color}
    />
  );
};